import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { Page } from '../Layout/PageLayout';
import { API_BASE } from '../../config/api';
import { getPasswordRequirements } from '../../utils/authValidation';

export const googleStartUrl = () =>
  `${API_BASE}/api/auth/google/start?client_url=${encodeURIComponent(window.location.origin)}`;

// Two-column auth layout (intro left, card right); `$centered` = card only.
export const AuthPage = styled(Page)`
  min-height: calc(100vh - var(--header-height));
`;

export const AuthLayout = styled.div`
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding: 5rem 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 3.5rem 6rem;
  align-items: ${(p) => (p.$top ? 'flex-start' : 'center')};
  justify-content: ${(p) => (p.$centered ? 'center' : 'flex-start')};
`;

export const AuthIntro = styled.div`
  flex: 1 1 26rem;
  min-width: 0;

  h1 {
    font-size: clamp(2.5rem, 5vw, 3.75rem);
    line-height: 1.04;
    letter-spacing: -0.035em;
    margin-bottom: 1.25rem;
  }

  > p {
    font-size: 1.125rem;
    max-width: 27.5rem;
    margin-bottom: 2.5rem;
  }
`;

export const IntroList = styled.ol`
  list-style: none;
  max-width: 27.5rem;
  border-top: 1px solid var(--border);

  li {
    display: flex;
    gap: 1rem;
    align-items: center;
    padding: 1rem 0;
    border-bottom: 1px solid var(--border);
  }

  span {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--text-tertiary);
    width: 1.5rem;
  }
`;

export const IntroNote = styled.div`
  max-width: 27.5rem;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.625rem;
  padding: 1.25rem 1.375rem;
  display: flex;
  gap: 0.875rem;

  p {
    font-size: 0.9375rem;
  }
`;

export const AuthCard = styled.section`
  flex: 0 1 27.5rem;
  min-width: 0;
  width: 100%;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.875rem;
  overflow: hidden;
`;

export const CardBody = styled.div`
  padding: 2rem;

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

export const CardFoot = styled.div`
  padding: 1.125rem 2rem;
  border-top: 1px solid var(--border);
  background: var(--surface-1);
  text-align: center;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

export const CardIcon = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: ${(p) => (p.$round ? '50%' : '0.625rem')};
  border: ${(p) => (p.$success ? '0' : '1px solid var(--border-accent)')};
  background: ${(p) => (p.$success ? 'rgba(61, 220, 132, 0.12)' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

export const PanelTitle = styled.h2`
  font-size: 1.75rem;
  letter-spacing: -0.02em;
  margin-bottom: 0.5rem;
`;

export const PanelText = styled.p`
  font-size: 0.9375rem;
  margin-bottom: 1.75rem;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 0.875rem;
    font-weight: 500;
  }
`;

export const FieldHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  a {
    font-size: 0.8125rem;
    color: var(--text-secondary);
    padding: 0.25rem 0;
  }

  a:hover {
    color: var(--brand);
  }
`;

export const Input = styled.input`
  width: 100%;
  min-height: 3.25rem;
  padding: 0 1rem;
  background: var(--background);
  border: 1px solid ${(p) => (p.$invalid ? 'var(--error)' : 'var(--border-accent)')};
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 1rem;
  transition: border-color var(--transition-fast);

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:focus {
    outline: none;
    border-color: var(--brand);
  }
`;

export const FieldError = styled.span`
  font-size: 0.8125rem;
  color: var(--error);
`;

const bigButton = css`
  width: 100%;
  min-height: 3.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  transition: background var(--transition-fast), border-color var(--transition-fast);
`;

export const PrimaryButton = styled.button`
  ${bigButton}
  background: var(--brand);
  color: var(--brand-ink);

  &:hover:not(:disabled) {
    background: #ff8f4f;
  }

  &:disabled {
    background: #1a1d22;
    color: var(--text-tertiary);
    cursor: not-allowed;
  }
`;

export const GhostButton = styled.button`
  ${bigButton}
  font-weight: 500;
  font-size: 0.9375rem;
  background: transparent;
  color: var(--text-primary);
  border: 1px solid var(--border-accent);

  &:hover {
    border-color: var(--text-tertiary);
  }
`;

export const GoogleButton = styled.a`
  ${bigButton}
  background: var(--text-primary);
  color: var(--brand-ink);

  &:hover {
    background: #ffffff;
  }

  span {
    font-weight: 700;
  }
`;

export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin: 1.5rem 0;
  color: var(--text-tertiary);
  font-size: 0.8125rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }
`;

export const MessageBox = styled.div`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--success)')};
  font-size: 0.875rem;
  padding: 0.75rem 0.875rem;
  background: ${(p) => (p.$error ? 'rgba(240, 82, 82, 0.08)' : 'rgba(61, 220, 132, 0.08)')};
  border: 1px solid ${(p) => (p.$error ? 'rgba(240, 82, 82, 0.3)' : 'rgba(61, 220, 132, 0.3)')};
  border-radius: 0.5rem;
  margin-bottom: 1.25rem;
`;

export const ReqChips = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

export const ReqChip = styled.li`
  font-size: 0.75rem;
  padding: 0.3125rem 0.625rem;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$ok ? 'rgba(61, 220, 132, 0.4)' : 'var(--border-accent)')};
  color: ${(p) => (p.$ok ? 'var(--success)' : 'var(--text-secondary)')};
  background: ${(p) => (p.$ok ? 'rgba(61, 220, 132, 0.08)' : 'transparent')};
`;

// Labels for authValidation.getPasswordRequirements keys.
export const REQUIREMENT_LABELS = [
  ['minLength', '8+ characters'],
  ['hasUppercase', 'Uppercase'],
  ['hasLowercase', 'Lowercase'],
  ['hasNumber', 'Number'],
  ['hasSpecial', 'Symbol'],
];

export const PasswordChips = ({ password, id }) => {
  const reqs = getPasswordRequirements(password);
  return (
    <ReqChips id={id} aria-live="polite">
      {REQUIREMENT_LABELS.map(([key, label]) => (
        <ReqChip key={key} $ok={reqs[key]}>
          {reqs[key] ? '✓ ' : ''}
          {label}
        </ReqChip>
      ))}
    </ReqChips>
  );
};

export const InlineLink = styled(Link)`
  color: var(--text-primary);
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: var(--brand);
  }
`;
